/**
 * One card's cycle as a horizontal bar (§13.1, §13.3).
 *
 * **The bar is positioned by date, not evenly divided** — the same rule the due
 * runway follows. The brass fill is how much of the cycle has already been spent
 * through, so a card two days from closing *looks* nearly full. That shape is the
 * whole reason to draw this rather than print three dates.
 *
 * **The rail past the close marker is dashed.** Everything right of it is
 * projection: the cycle hasn't closed, the statement hasn't been issued, and the
 * due date is arithmetic from the card's own history (§2.7). The solid/dashed
 * break is the line between what the bank has said and what MyDeck worked out.
 *
 * **Dates are labelled underneath in a fixed grid, not positioned on the rail.**
 * Positioned labels overlap the moment two dates bunch — which they do, since
 * "today" sits between them by definition — and a timeline whose labels collide
 * is worse than a table. The rail carries the proportion; the grid carries the
 * dates; markers carry the detail on hover.
 */

import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";
import type { CycleCard } from "@/lib/api/client";

const DAY_MS = 86_400_000;

function daysBetween(from: string, to: string): number {
  return Math.round(
    (new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / DAY_MS,
  );
}

function pct(day: number, span: number): number {
  return Math.max(0, Math.min(100, (day / span) * 100));
}

export function CycleBar({ card, today }: { card: CycleCard; today: string }) {
  if (!card.cycle_start || !card.cycle_end) return null;
  const start = card.cycle_start;

  const closeDay = daysBetween(start, card.cycle_end);
  const dueDay = card.due_expected_on ? daysBetween(start, card.due_expected_on) : closeDay;
  const todayDay = daysBetween(start, today);
  // The rail runs to the projected due date — the horizon a purchase decision
  // actually cares about, not the cycle close.
  const span = Math.max(dueDay, closeDay, todayDay, 1);

  const closeAt = pct(closeDay, span);
  const todayAt = pct(todayDay, span);

  // A printed, still-owed bill belongs to the PREVIOUS cycle, so it sits early
  // on this rail. Showing only the projected due would hide the bill that is
  // actually coming up.
  const owedDay = card.outstanding_due_date
    ? daysBetween(start, card.outstanding_due_date)
    : null;
  const showOwed =
    owedDay !== null && card.outstanding !== null && owedDay >= todayDay && owedDay <= span;

  return (
    <div>
      <div className="relative h-4" role="img" aria-label={cycleAria(card, today)}>
        {/* Rail: solid to the close, dashed after. */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-1.5 h-1.5 overflow-hidden rounded-full bg-raised"
        >
          <div className="h-full bg-hairline-strong/60" style={{ width: `${closeAt}%` }} />
        </div>
        <div
          aria-hidden
          className="absolute top-[0.5625rem] border-t border-dashed border-hairline-strong"
          style={{ left: `${closeAt}%`, right: 0 }}
        />

        {/* Elapsed — brass, the part that has already happened. */}
        <div
          aria-hidden
          className="absolute top-1.5 h-1.5 rounded-full bg-brass/50"
          style={{ width: `${todayAt}%` }}
        />

        {/* Today. */}
        <span
          className="absolute top-0 h-4 w-0.5 -translate-x-1/2 rounded bg-brass"
          style={{ left: `${todayAt}%` }}
          title={`Today, ${formatDate(today)} — day ${card.days_into_cycle} of ${card.cycle_length_days}`}
        />

        {/* Cycle close: hollow, because it hasn't happened. */}
        <span
          className="absolute top-0.5 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-hairline-strong bg-panel"
          style={{ left: `${closeAt}%` }}
          title={`Cycle closes ${formatDate(card.cycle_end)}. ${card.close_basis}`}
        />

        {/* A bill the bank has already printed and that is still owed: filled. */}
        {showOwed && (
          <span
            className="absolute top-0.5 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-warn bg-warn"
            style={{ left: `${pct(owedDay, span)}%` }}
            title={`${formatMoney(card.outstanding)} due ${formatDate(
              card.outstanding_due_date,
            )} — printed on the statement dated ${formatDate(card.last_statement_date)}.`}
          />
        )}
      </div>

      {/* The dates. Fixed grid, so nothing can ever overlap. */}
      <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
        <Leg label="Opened" value={formatDate(card.cycle_start)} />
        <Leg
          label="Closes"
          value={formatDate(card.cycle_end)}
          note={
            card.days_left_in_cycle === 0
              ? "today"
              : `${card.days_left_in_cycle}d left`
          }
          align="center"
        />
        <Leg
          label="Bill due"
          value={card.due_expected_on ? formatDate(card.due_expected_on) : "—"}
          note={card.due_expected_on ? "projected" : "no due date learned"}
          align="right"
          projected
        />
      </dl>
    </div>
  );
}

function Leg({
  label,
  value,
  note,
  align = "left",
  projected = false,
}: {
  label: string;
  value: string;
  note?: string;
  align?: "left" | "center" | "right";
  projected?: boolean;
}) {
  return (
    <div
      className={cn(
        "min-w-0",
        align === "center" && "text-center",
        align === "right" && "text-right",
      )}
    >
      <dt className="label-micro">{label}</dt>
      <dd
        className={cn(
          "truncate tabular-nums",
          projected ? "text-muted" : "text-ink",
        )}
      >
        {value}
      </dd>
      {note && <dd className="truncate text-micro text-faint">{note}</dd>}
    </div>
  );
}

/** The rail is decorative; this sentence is what a screen reader gets instead. */
function cycleAria(card: CycleCard, today: string): string {
  const parts = [
    `Cycle opened ${formatDate(card.cycle_start)}`,
    `today is ${formatDate(today)}`,
    `closes ${formatDate(card.cycle_end)}`,
  ];
  if (card.due_expected_on) {
    parts.push(`bill projected due ${formatDate(card.due_expected_on)}`);
  }
  return `${parts.join(", ")}.`;
}
